import type { FetchError } from 'ofetch'
import type { PlayerAccountClient } from '../types'
import { claimCachedDocumentAuthReload, defineNuxtPlugin, hasSessionCookie, usePlayerAccountClient, USER_INFO_KEY } from '#imports'

type UserInfo = PlayerAccountClient.components['schemas']['UserInfoResponse']

/**
 * Back/forward and tab restores may reuse a `private, no-cache` document from the HTTP cache, and
 * its payload still holds the player of its first render. The session is checked again before it
 * hydrates, so an ended session does not fire every logged-in query.
 */
export default defineNuxtPlugin({
    name: 'core:cached-document',
    enforce: 'pre',
    async setup(nuxtApp) {
        const cached = nuxtApp.payload.data[USER_INFO_KEY] as UserInfo | undefined
        if (!cached) return

        const [navigation] = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[]

        // Nothing crossed the network: the document came from the HTTP cache.
        if (navigation?.transferSize !== 0) return

        // Without the session cookie the session has ended; with it, the server may still have ended it.
        if (hasSessionCookie()) {
            // Not useUserInfo: it answers from this same payload. The 401 is handled here, as the
            // client's own redirect needs i18n, whose plugin runs later.
            const { getUserInfo } = usePlayerAccountClient({ ignoreUnauthorized: true })

            try {
                const user = await getUserInfo(AbortSignal.timeout(3_000))

                // Another player's answer would leave the rest of the payload with the cached one.
                if (user?.id === cached.id) {
                    nuxtApp.payload.data[USER_INFO_KEY] = user
                    return
                }
            }
            catch (error) {
                // Only a 401 says the session ended; anything else keeps the cached player.
                if ((error as FetchError).status !== 401) return
            }
        }

        // Hydrates as it is when a recent reload came back from the cache again, or when no
        // marker can be stored to tell the next load so.
        if (!claimCachedDocumentAuthReload()) return

        // A reload revalidates the document, so the server renders it for the current session.
        window.location.reload()
        await new Promise(() => {})
    },
})
